import React from 'react';
import { IGlobalStorefrontSettings, IStorefrontLink, IStorefrontSettings, IStorefrontTheme } from '../../../../models/Storefront';
import { Callout, ContextualMenuItemType, DefaultButton, DirectionalHint, IContextualMenuItem, IconButton, IRawStyle, TextField, Toggle } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import {
    useDeleteStorefrontHeaderLinkMutation,
    useSaveStorefrontHeaderLinkMutation,
    useSaveStorefrontHeaderLinksMutation
} from '../../../../store/endpoints/StorefrontLinkEndpoints';
import StorefrontColorPickerButton from '../StorefrontColorPickerButton';
import { localizerFor } from '../localizationConfig';

interface IStorefrontHeaderLinksEditorProps {
    storefrontSettings: IStorefrontSettings;
    storefrontLinks: IStorefrontLink[];
    globalLinks: IStorefrontLink[];
    onLinksChange: (newLinks: IStorefrontLink[]) => void;
    storefrontTheme?: IStorefrontTheme;
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    // Darkness of the BAR (drives the Menu button chrome, which sits on the bar).
    isDark?: boolean;
    // Darkness of the POPOVER surface (menuBg) — separate from the bar. The dropdown panel can be
    // dark while the bar is light (e.g. an amber bar with a dark menu panel), so the popover's dark
    // treatment must key off this, not the bar; otherwise the light-surface default shows through
    // and the (readable-on-dark, i.e. white) entry text lands on white and vanishes.
    menuIsDark?: boolean;
    buttonStyle?: React.CSSProperties;
    menuHeaderStyle?: React.CSSProperties;
    menuItemStyle?: React.CSSProperties;
    menuPopoverStyle?: React.CSSProperties;
    menuItemColor?: string;
    menuHeaderColor?: string;
}

const StorefrontHeaderLinksEditor: React.FC<IStorefrontHeaderLinksEditorProps> = (props) => {
    const [saveLink] = useSaveStorefrontHeaderLinkMutation();
    const [saveLinks] = useSaveStorefrontHeaderLinksMutation();
    const [deleteLink] = useDeleteStorefrontHeaderLinkMutation();
    const storefront = props.storefrontSettings.id ?? '';
    const menuButtonId = useId('header-links-menu');
    // The Menu button's label is shopper-facing (this store calls it "Directives"), so it resolves
    // through the storefront's localization overrides rather than being hardcoded English.
    const L = React.useMemo(
        () => localizerFor(props.storefrontSettings.localizationConfig),
        [props.storefrontSettings.localizationConfig]);

    const [editingLink, setEditingLink] = React.useState<IStorefrontLink | null>(null);
    const [editText, setEditText] = React.useState('');
    const [editUrl, setEditUrl] = React.useState('');
    const [editSeparator, setEditSeparator] = React.useState(false);
    const [editColor, setEditColor] = React.useState('');

    const globalLinks = props.storefrontSettings.ignoreGlobalLinks ? [] : props.globalLinks.filter(l => !l.disabled);
    const sfLinks = props.storefrontLinks;

    if (!props.editMode && globalLinks.length === 0 && sfLinks.filter(l => !l.disabled).length === 0) return null;

    const openEdit = (link: IStorefrontLink) => {
        setEditingLink(link);
        setEditText(link.text ?? '');
        setEditUrl(link.url ?? '');
        setEditSeparator(link.isSeparator ?? false);
        setEditColor(link.color ?? '');
    };

    const commitEdit = () => {
        if (!editingLink) return;
        saveLink({
            link: { ...editingLink, text: editText, url: editUrl, isSeparator: editSeparator, color: editColor || undefined },
            storefront,
        });
        setEditingLink(null);
    };

    const handleMoveUp = (idx: number) => {
        const links = [...sfLinks];
        const [moved] = links.splice(idx, 1);
        links.splice(idx - 1, 0, moved);
        saveLinks({ links: links.map((l, i) => ({ ...l, sortOrder: i + 1 })), storefront });
    };

    const handleMoveDown = (idx: number) => {
        const links = [...sfLinks];
        const [moved] = links.splice(idx, 1);
        links.splice(idx + 1, 0, moved);
        saveLinks({ links: links.map((l, i) => ({ ...l, sortOrder: i + 1 })), storefront });
    };

    const handleAdd = async () => {
        const newLink: IStorefrontLink = {
            text: 'New Link',
            url: '',
            sortOrder: sfLinks.length + 1,
            storefront,
            type: 'header',
            disabled: false,
            shared: false,
            isSeparator: false,
        };
        const result = await saveLink({ link: newLink, storefront }).unwrap();
        openEdit({ ...newLink, id: result.id });
    };

    const buildMenuItems = (): IContextualMenuItem[] => {
        if (!props.editMode) {
            const allLinks = [...globalLinks, ...sfLinks.filter(l => !l.disabled)];
            return allLinks.map(link => ({
                key: link.id ?? `${link.text}-${link.sortOrder}`,
                text: link.text ?? '',
                itemType: link.isSeparator ? ContextualMenuItemType.Header : undefined,
                href: !link.isSeparator ? link.url : undefined,
                // Mirror the live storefront: render section headers / links with the merchant's
                // menuHeader / menuItem styling so the preview's dropdown matches what shoppers see.
                onRender: () => (
                    link.isSeparator
                        ? <div style={{ padding: '5px 12px', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', opacity: 0.85, color: props.menuHeaderColor, ...props.menuHeaderStyle, ...(link.color ? { color: link.color } : {}) }}>{link.text}</div>
                        : <a href={link.url} style={{ display: 'block', padding: '6px 12px', textDecoration: 'none', color: props.menuItemColor ?? 'inherit', fontSize: 14, ...props.menuItemStyle, ...(link.color ? { color: link.color } : {}) }} onClick={(e) => e.preventDefault()}>{link.text}</a>
                ),
            }));
        }

        const items: IContextualMenuItem[] = [];

        if (globalLinks.length > 0) {
            globalLinks.forEach(link => {
                items.push({
                    key: `g-${link.id}`,
                    text: link.text ?? '',
                    itemType: link.isSeparator ? ContextualMenuItemType.Header : undefined,
                    disabled: true,
                    title: 'Global link — edit in global settings',
                });
            });
            if (sfLinks.length > 0) {
                items.push({ key: 'g-divider', text: '-', itemType: ContextualMenuItemType.Divider });
            }
        }

        sfLinks.forEach((link, idx) => {
            items.push({
                key: `sf-${link.id ?? idx}`,
                text: link.text ?? '',
                onRender: () => (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 4px 0 12px',
                            height: 36,
                            gap: 2,
                            opacity: link.disabled ? 0.5 : 1,
                            minWidth: 240,
                        }}
                    >
                        <span style={{ flex: 1, fontWeight: link.isSeparator ? 'bold' : 'normal', fontSize: 14, userSelect: 'none' }}>
                            {link.text || '(empty)'}
                        </span>
                        <IconButton
                            iconProps={{ iconName: 'ChevronUp' }}
                            title='Move up'
                            disabled={idx === 0}
                            styles={{ root: { height: 24, width: 24, minWidth: 0 } }}
                            onClick={(e) => { e.stopPropagation(); handleMoveUp(idx); }}
                        />
                        <IconButton
                            iconProps={{ iconName: 'ChevronDown' }}
                            title='Move down'
                            disabled={idx === sfLinks.length - 1}
                            styles={{ root: { height: 24, width: 24, minWidth: 0 } }}
                            onClick={(e) => { e.stopPropagation(); handleMoveDown(idx); }}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            title='Edit link'
                            styles={{ root: { height: 24, width: 24, minWidth: 0 } }}
                            onClick={(e) => { e.stopPropagation(); openEdit(link); }}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            title='Delete link'
                            styles={{ root: { height: 24, width: 24, minWidth: 0 } }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (link.id) deleteLink({ storefront, id: link.id });
                            }}
                        />
                    </div>
                ),
            });
        });

        items.push({
            key: 'add',
            text: 'Add Link',
            iconProps: { iconName: 'Add' },
            onClick: () => { handleAdd(); },
        });

        return items;
    };

    return (
        <>
            <DefaultButton
                id={menuButtonId}
                text={L('localization_nav_menu_button')}
                style={props.buttonStyle}
                className={props.isDark ? 'storefront-header-menu-button-dark' : undefined}
                menuProps={{
                    items: buildMenuItems(),
                    // The popover surface (bg + orange ring) is the Callout's `calloutMain` slot, not
                    // its outer root (which Fluent overwrites with open/close animation styles). Style
                    // that slot so the preview's dropdown mirrors the live storefront's themed popover.
                    calloutProps: {
                        // Key the dark-surface treatment off the POPOVER's darkness (menuBg), not the
                        // bar's — the panel can be dark under a light bar. This makes Fluent's default
                        // white ContextualMenu surface transparent so the themed dark popover shows and
                        // the white entry text is legible.
                        className: (props.menuIsDark ?? props.isDark) ? 'storefront-header-menu-callout-dark' : undefined,
                        styles: props.menuPopoverStyle ? { calloutMain: props.menuPopoverStyle as IRawStyle } : undefined,
                    },
                }}
            />
            {editingLink && (
                <Callout
                    target={`#${menuButtonId}`}
                    onDismiss={commitEdit}
                    setInitialFocus
                    directionalHint={DirectionalHint.bottomCenter}
                    style={{ padding: '1em', minWidth: 280 }}
                >
                    <h3 style={{ margin: '0 0 0.75em 0' }}>Edit Link</h3>
                    <Toggle
                        label='Section Header'
                        inlineLabel
                        checked={editSeparator}
                        onChange={(_, v) => setEditSeparator(v ?? false)}
                    />
                    <TextField
                        label='Text'
                        value={editText}
                        onChange={(_, v) => setEditText(v ?? '')}
                        style={{ marginTop: '0.5em' }}
                    />
                    {!editSeparator && (
                        <TextField
                            label='Hyperlink'
                            value={editUrl}
                            onChange={(_, v) => setEditUrl(v ?? '')}
                            style={{ marginTop: '0.5em' }}
                        />
                    )}
                    <div style={{ marginTop: '0.75em' }}>
                        <StorefrontColorPickerButton
                            label='Text Color'
                            entries={[{
                                label: 'Text Color',
                                type: 'color',
                                value: editColor,
                                onChange: (v) => setEditColor(v),
                                onClear: () => setEditColor(''),
                            }]}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75em', gap: 4 }}>
                        <DefaultButton text='Save' onClick={commitEdit} />
                    </div>
                </Callout>
            )}
        </>
    );
};

export default StorefrontHeaderLinksEditor;
